<template>
  <LISAUiConfig :label-placement="'outside'" :theme="isLISA3Dark ? 'dark' : undefined">
    <div
      ref="appDiv"
      :style="`display: grid; grid-template-rows: ${
        isMonitorViewEnabled ? '0' : '3.5em'
      } minmax(0, auto) 0`"
      class="h-100 overflow-y-hidden"
      :class="{
        'fake-dark-selector': isDarkModeEnabled,
      }"
    >
      <div class="header">
        <HeadNavbar
          v-show="!isMonitorViewEnabled"
          v-model:show-sidebar="showSidebar"
          @open-global-search="showNewGlobalSearch = true"
        />
      </div>
      <div class="main h-100">
        <NewSidebarComponent
          v-if="!isMonitorViewEnabled && showSidebar && useNewSidebar"
          class="doNotPrintMe"
          @open-command-menu="showLegacyCommandMenu = true"
        />
        <SidebarComponent
          v-else-if="!isMonitorViewEnabled && showSidebar"
          class="doNotPrintMe"
          @open-command-menu="showLegacyCommandMenu = true"
        />
        <LISAThemeProvider class="rightContainer">
          <router-view
            v-if="showAuthorizedRoute"
            ref="mainRouterView"
            class="router-view"
            :class="{
              'background-image': useBackgroundImage,
              isEmma: ENV.VITE_APP_BRAND_NAME === 'EMMA' && useBackgroundImage,
            }"
          />

          <KeyCloakUserDialog />
        </LISAThemeProvider>
      </div>

      <Placeholder />
      <HalloweenArchiveSpider v-if="isModifierSet(ModifierType.halloweenSpider)" />
      <RFIDScanComponent ref="rfidComponent" />

      <Teleport :to="feedbackDialog ?? 'body'" :disabled="feedbackDialog == null">
        <LISAButton
          v-tooltip.top="t('sentry.formTitle')"
          :popover="feedbackDialog ? 'manual' : undefined"
          class="p-1 m-0 SentryFeedbackButton doNotPrintMe mb-3"
          :class="{
            floatingSentryButton: true,
            SentryErrorGlow: sentryGlow,
          }"
          style="z-index: 10000"
          @click.stop.prevent
        >
          <i style="color: black" class="fas fa-bug" />
        </LISAButton>
      </Teleport>
      <div id="LightBoxPortal" />
      <Toast position="bottom-right" :base-z-index="20000" />
      <ConfirmDialog
        :pt="{
          pcRejectButton: {
            root: {
              'data-testid': 'pcRejectButton',
            },
          },
          pcAcceptButton: {
            root: {
              'data-testid': 'pcAcceptButton',
            },
          },
        }"
      />
      <NewCommandMenu v-if="useNewGlobalSearch" v-model="showNewGlobalSearch" />
      <CommandVue v-model="showLegacyCommandMenu" :enable-hotkey="!useNewGlobalSearch" />
      <LisaControl />
      <DataTestIdBadge v-if="isTest" :interval-ms="500" :only-visible="true" :offset-y="-14" />
      <VueComponentNameBadge v-if="isDev" />
    </div>
  </LISAUiConfig>
</template>

<script setup lang="ts">
import { useStore } from "vuex";
import { DefaultApolloClient, provideApolloClient } from "@vue/apollo-composable";
import {
  provide,
  onMounted,
  ref,
  computed,
  watch,
  onUnmounted,
  shallowRef,
  nextTick,
  useTemplateRef,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import Toast, { type ToastMessageOptions } from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { useTranslation } from "@/language/translation";
// eslint-disable-next-line no-restricted-imports
import { useConfirm } from "primevue/useconfirm";
import ConfirmDialog from "primevue/confirmdialog";
import SidebarComponent from "@/components/menu/SidebarComponent.vue";
import NewSidebarComponent from "@/components/menu/NewSidebarComponent.vue";
import HeadNavbar from "@/components/HeadNavbar.vue";
import Placeholder from "@/components/shared/PlaceholderComponent.vue";
import HalloweenArchiveSpider from "@/components/shared/HalloweenArchiveSpider.vue";
import lisaBG from "@/assets/logos/lisa.png";
import emmaBG from "@/assets/logos/lisa.png";
import smoothScroll from "@/functions/smoothScroll";
import { apolloClient } from "./graphql/apollo";
import RFIDScanComponent from "./components/rfid/RFIDScanComponent.vue";
import CommandVue from "./modules/commandMenu/views/CommandMenu.vue";
import NewCommandMenu from "./modules/commandMenu/views/NewCommandMenu.vue";
import isModifierSet from "./functions/userModifier";
import { ModifierType } from "./functions/userModifierEnum";
import { setInitialScreenshotContainer } from "./functions/ScreenshotHelper";
import LISAThemeProvider from "./modules/theme/components/LISAThemeProvider.vue";
import { useSentryFeedback, moveOpenFeedbackForm } from "./modules/sentry/sentryFeedback";
import LISAButton from "./components/shared/primevue/LISAButton.vue";
import Tooltip from "primevue/tooltip";
import KeyCloakUserDialog from "./modules/keycloak/components/KeyCloakUserDialog.vue";
import LisaControl from "./components/LisaControl.vue";
import DataTestIdBadge from "./modules/devTools/components/DataTestIdBadge.vue";
import VueComponentNameBadge from "./modules/devTools/components/VueComponentNameBadge.vue";
import { ENV } from "./ENV";
import { confirmModal } from "./components/shared/form/ConfirmationModal";
import { captureException } from "@sentry/browser";
import { useDynamicTranslationCatalogSync } from "@/language/useDynamicTranslationCatalogSync";
import { isIntern, isProduction } from "./functions/environment.ts";
import { useAuthorization } from "./functions/useAuthorization";
import { routerRightCheck, type RouterAccessRightMetaType } from "./router";
import { watchRouteRevocation } from "./functions/routeAuthorization";
import { LISAUiConfig } from "@fi-solutions/lisa-ui";
import { isLISA3, isLISA3Dark } from "./composables/useLISA3.ts";
import { provideMenuNotificationBadges } from "./functions/useMenuNotificationBadges";
import { MonitorViewInjectionKey } from "./functions/monitorViewInjectionKey";

defineOptions({ name: "App" });

const vTooltip = Tooltip;
const rfidComponent = useTemplateRef("rfidComponent");
provide("lisaLogo", () => (ENV.VITE_APP_BRAND_NAME === "EMMA" ? emmaBG : lisaBG));
provide("smoothScroll", (top: number | undefined) => smoothScroll(top));
provide("disableRFID", () => rfidComponent.value?.disableRFID());
provide("enableRFID", () => rfidComponent.value?.enableRFID());
const showLegacyCommandMenu = ref(false);
const showNewGlobalSearch = ref(false);
const monitorViewRef = ref(false);
const route = useRoute();
const router = useRouter();
const authorization = useAuthorization();
const showAuthorizedRoute = computed(() =>
  routerRightCheck(
    route.meta as RouterAccessRightMetaType,
    route.params.siteId ??
      (typeof route.query.siteId === "string" ? route.query.siteId : undefined),
  ),
);
watchRouteRevocation(
  () => ({
    revision: authorization.snapshot.value?.revision,
    stale: authorization.stale.value,
    path: route.fullPath,
    ready: authorization.ready.value,
    guarded: Boolean(route.meta.rights),
    permitted: showAuthorizedRoute.value,
  }),
  () => void router.replace("/404"),
);
const store = useStore();
const toast = useToast();
const isTest = computed(() => import.meta.env.MODE === "test");
const isDev = import.meta.env.DEV;
window.$toast = (message: ToastMessageOptions) => {
  /**
   * providing toast to be usable in apollo and api,
   * setting defaults.
   */
  return toast.add({
    life: 3000,
    ...message,
  });
};
const translation = useTranslation();

window.$confirm = useConfirm();

const isMonitorViewEnabled = computed({
  get(): boolean {
    return (
      (route.meta?.hideNavbar as boolean) ||
      (((route.query.monitorView != null && route.query.monitorView === "true") ||
        monitorViewRef.value) &&
        (route.meta?.supportsMonitorView as boolean))
    );
  },
  set(value) {
    monitorViewRef.value = value;
  },
});
provide(MonitorViewInjectionKey, isMonitorViewEnabled);

const useBackgroundImage = computed(() => Boolean(route.meta?.backgroundImage));

provide(DefaultApolloClient, apolloClient);
useDynamicTranslationCatalogSync();

const showSidebar = ref(true);

const appDiv = useTemplateRef<HTMLDivElement>("appDiv");

setInitialScreenshotContainer(
  computed(() => {
    return appDiv.value!;
  }),
);

// SENTRY
const { attachToHTMLElement, floatingButton } = useSentryFeedback(".SentryFeedbackButton");
const sentryGlow = computed(() => floatingButton.isGlowing.value);
const feedbackDialog = shallowRef<HTMLDialogElement | null>(null);
let feedbackDialogObserver: MutationObserver | undefined;
onMounted(() => {
  const openDialogs = new Set<HTMLDialogElement>();
  const updateFeedbackDialog = () => {
    const dialogs = document.querySelectorAll<HTMLDialogElement>("dialog.lisa-dialog[open]");
    for (const dialog of openDialogs) {
      if (!dialog.open || !dialog.isConnected) openDialogs.delete(dialog);
    }
    for (const dialog of dialogs) openDialogs.add(dialog);
    feedbackDialog.value = Array.from(openDialogs).at(-1) ?? null;
  };
  feedbackDialogObserver = new MutationObserver(updateFeedbackDialog);
  feedbackDialogObserver.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["open"],
  });
  updateFeedbackDialog();
});
watch(feedbackDialog, async (dialog) => {
  await nextTick();
  if (dialog) {
    document.querySelector<HTMLButtonElement>(".SentryFeedbackButton")?.showPopover();
  }
  moveOpenFeedbackForm(dialog);
});
onUnmounted(() => feedbackDialogObserver?.disconnect());
watch(
  [translation.locale, isLISA3],
  () => {
    attachToHTMLElement();
  },
  {
    immediate: true,
  },
);
onMounted(async () => {
  store.dispatch("clientCache/clearEventExportPromise");
});

const isDarkModeEnabled = computed(() => {
  return store.state.login.user?.darkMode ?? false;
});
const useNewGlobalSearch = computed(() => isModifierSet(ModifierType.LISA3));
const useNewSidebar = computed(() => isModifierSet(ModifierType.LISA3));
provideApolloClient(apolloClient)(() => provideMenuNotificationBadges(isLISA3));

const { t } = useTranslation();

async function checkVersion() {
  if (import.meta.env.MODE !== "production") {
    return;
  }
  try {
    const res = await fetch("/version", { cache: "no-store" });
    if (!res.ok) {
      throw new Error(`Version check failed with status ${res.status}`);
    }

    const data = await res.json();

    if (data.currentVersion !== import.meta.env.__VERSION__.trim()) {
      confirmModal({
        header:
          t("global.newVersionAvailableHeader", { version: data.currentVersion }) ||
          `New Update (${data.currentVersion})`,
        message:
          t("global.newVersionAvailable") ||
          `A new version is available. Please refresh the page to access all features.
If you have any unsaved changes, please save or copy them before refreshing the page.`,
        acceptLabel: t("global.reload") || `Refresh`,
        rejectLabel: t("global.reloadLater") || `Reload later`,
        accept: () => {
          window.location.reload();
        },
      });
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("Version check failed", e);
  }
}

function preloadListenerFunction(
  event: Event & {
    payload: Error;
  },
) {
  event.preventDefault();

  const key = "vite-preload-reload";
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, "1");
    if (isProduction() && !isIntern()) {
      captureException("Here we would do a hard reload!");
    } else {
      window.location.reload();
    }
  } else {
    captureException("Chunk reload failed after refresh");
    // eslint-disable-next-line no-console
    console.error("Chunk reload failed after refresh", event.payload);
  }
}

let intervalId: ReturnType<typeof setInterval> | undefined;

onMounted(() => {
  checkVersion();
  intervalId = setInterval(checkVersion, 60_000 * 5);
  window.addEventListener("vite:preloadError", preloadListenerFunction);
});

onUnmounted(() => {
  if (intervalId != null) {
    clearInterval(intervalId);
  }
  window.removeEventListener("vite:preloadError", preloadListenerFunction);
});
</script>

<style scoped>
.isEmma {
  background-image: url("@/assets/background_emma.png");
}
@keyframes errorGlow {
  0% {
    box-shadow: 0 0 0px var(--p-danger-color);
    border-color: var(--p-danger-color);
  }
  50% {
    box-shadow: 0 0 5px 3px var(--p-danger-color);
  }
  100% {
    box-shadow: 0 0 0px var(--p-danger-color);
  }
}

.SentryErrorGlow {
  animation: errorGlow 1s ease-in-out normal;
}
</style>

<style lang="scss">
body {
  margin: 0;
}
</style>
<style>
@media (prefers-reduced-motion: reduce) {
  .p-component,
  .p-dialog,
  .p-overlay,
  .p-dropdown-panel {
    animation-duration: 0s !important;
    transition-duration: 0s !important;
  }
}

.floatingSentryButton {
  position: fixed;
  bottom: 2.2rem;
  left: 0.5rem;
  z-index: 10000;
  background-color: #f2f2f2 !important;
  border: 1px solid transparent;
}

.floatingSentryButton[popover] {
  inset: auto auto 2.2rem 0.5rem;
  margin: 0;
}

#sentry-feedback[popover] {
  inset: 0;
  width: 100vw;
  height: 100dvh;
  margin: 0;
  padding: 0;
  overflow: visible;
  border: 0;
  background: transparent;
}
</style>

<style lang="scss" scoped>
.main {
  display: flex;
  width: 100vw;

  .rightContainer {
    position: relative;
    flex-grow: 1;
    overflow-y: hidden;
    overflow-x: hidden;
    > * {
      overflow-y: auto;
    }
  }
}
</style>
